import { CardSpacer } from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Metadata, { MetadataFormData } from "@saleor/components/Metadata";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import SeoForm from "@saleor/components/SeoForm";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import useMetadataChangeTrigger from "@saleor/utils/metadata/useMetadataChangeTrigger";
import { ContentState, convertToRaw, RawDraftContentState } from "draft-js";
import React from "react";
import { useIntl } from "react-intl";

import CategoryDetailsForm from "../../components/CategoryDetailsForm";
import CategoryStoreType from "../../components/CategoryStoreType";
import CategoryHeader from "../CategoryHeader";

export interface FormData extends MetadataFormData {
  description: RawDraftContentState;
  backgroundImageAlt: string;
  name: string;
  color: string;
  iconImage: {
    alt: string;
    url: string;
  };
  slug: string;
  seoTitle: string;
  seoDescription: string;
  smMarketsId: number;
  storeType: string;
}

const initialData: FormData = {
  backgroundImageAlt: "",
  color: "",
  description: convertToRaw(ContentState.createFromText("")),
  iconImage: {
    alt: null,
    url: null
  },
  metadata: [],
  name: "",
  privateMetadata: [],
  seoDescription: "",
  seoTitle: "",
  slug: "",
  smMarketsId: null,
  storeType: ""
};

const breadcrumbDirectory = [
  {
    name: "Categories",
    url: "/categories"
  },
  {
    name: "Create Category"
  }
];

export interface CategoryCreatePageProps {
  errors: ProductErrorFragment[];
  disabled: boolean;
  storeType?: string;
  saveButtonBarState: ConfirmButtonTransitionState;
  onIconUpload: (file: File) => void;
  onSubmit: (data: FormData) => void;
  onBack();
}

export const CategoryCreatePage: React.FC<CategoryCreatePageProps> = ({
  disabled,
  onIconUpload,
  onSubmit,
  storeType,
  onBack,
  errors,
  saveButtonBarState
}) => {
  const intl = useIntl();
  const {
    makeChangeHandler: makeMetadataChangeHandler
  } = useMetadataChangeTrigger();

  const blank = true;

  return (
    <Form onSubmit={onSubmit} initial={initialData} confirmLeave>
      {({ data, change, submit, hasChanged }) => {
        const changeMetadata = makeMetadataChangeHandler(change);

        if (data.storeType === "" && storeType) {
          data.storeType = storeType;
        }

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader
                title={intl.formatMessage({
                  defaultMessage: "Create Category",
                  description: "page header"
                })}
              />
            </div>
            <div>
              <CategoryDetailsForm
                disabled={disabled}
                data={data}
                onChange={change}
                errors={errors}
              />
              <CardSpacer />
              <CategoryStoreType
                disabled={storeType ? true : false}
                data={data}
                id={null}
                onChange={change}
              />
              {!blank ? (
                <div>
                  <SeoForm
                    allowEmptySlug={true}
                    helperText={intl.formatMessage({
                      defaultMessage:
                        "Add search engine title and description to make this category easier to find"
                    })}
                    slug={data.slug}
                    slugPlaceholder={data.name}
                    title={data.seoTitle}
                    titlePlaceholder={data.name}
                    description={data.seoDescription}
                    descriptionPlaceholder={data.name}
                    loading={disabled}
                    onChange={change}
                    disabled={disabled}
                  />
                  <CardSpacer />
                  <Metadata data={data} onChange={changeMetadata} />
                </div>
              ) : null}
              <CategoryHeader
                data={data}
                onChange={change}
                onIconUpload={file => onIconUpload(file)}
              />
              <SaveButtonBar
                onCancel={onBack}
                onSave={submit}
                state={saveButtonBarState}
                disabled={disabled || !hasChanged}
              />
            </div>
          </Container>
        );
      }}
    </Form>
  );
};
CategoryCreatePage.displayName = "CategoryCreatePage";
export default CategoryCreatePage;
